import { hasUrlCredential } from "@/lib/analytics/redactUrl";

/** How often the gate re-reads the URL. */
const POLL_MS = 250;

/**
 * Run Session Replay only while the URL carries no credential.
 *
 * rrweb records the page URL in its **Meta** frame — `{ type: 4, data: { href } }` — and that
 * frame reaches the event buffer unredacted: the recording payload's only hook,
 * `beforeAddRecordingEvent`, is applied to CUSTOM frames alone (`maybeApplyCallback` guards on
 * `isCustomEvent`, i.e. `type === 5`, in `@sentry-internal/replay` 10.17). So a Meta frame
 * emitted while `location.href` is `/auth/callback#access_token=…`, `/invite/accept?token=…` or
 * `/share/:token` uploads that credential whole, and no callback anywhere can stop it —
 * `replaysOnErrorSampleRate: 1.0` means one error on the page is enough.
 *
 * That frame is not emitted only at start. It rides every full snapshot, and error-sampling mode
 * forces one every `BUFFER_CHECKOUT_TIME` = 60s (`startRecording()` overwrites `checkoutEveryNms`
 * while `recordingMode === 'buffer'`); session mode re-snapshots when it refreshes the session.
 * So gating the start alone would still ship the credential of any page the app navigates ONTO
 * after a clean start, which is the shape of every credential route we link to internally:
 * `/login` → `/invite/accept?token=…` (InviteAccept, SignUp) and `/share/:token`. Hence the gate
 * runs for the whole page load — it waits for a clean URL to start, and stops recording again the
 * moment one carries a credential.
 *
 * Stripping the credential first is not an option: supabase-js needs the fragment
 * (`detectSessionInUrl` consumes it asynchronously, and ResetPassword hand-parses it), and the
 * invite / share pages read their token straight out of the URL. So the recorder waits, and the
 * cost is the frames before the app navigates away from the credential.
 *
 * The wait has no deadline. A credential can sit in the URL for as long as the user takes to
 * act on the page — `/invite/accept?token=…` keeps its token until the invitee clicks — and a
 * gate that gave up would cost that session both replay AND its `replaysOnErrorSampleRate: 1.0`
 * error replays for the rest of the page load, precisely on the flows worth recording. On
 * `/share/:token`, where the token never leaves the URL, the session is never recorded, which
 * is the point; the poll it leaves running is a string check four times a second.
 *
 * Whether stopping is FINAL is the caller's, because it turns on what its `stop()` costs.
 * Sentry's is final (the default): `stop()` is a teardown, not `pause()`, and restarting would
 * re-roll the sampling decision `replaysSessionSampleRate` / `replaysOnErrorSampleRate` already
 * made — `start()` would force a full session replay on everyone who ever opens an invite,
 * `startBuffering()` would quietly drop them out of session sampling. Losing the rest of the page
 * load is the same trade this gate already makes for a session that opens on a share link.
 * PostHog's `startSessionRecording()` re-rolls nothing, so it passes `resumable` and the poll
 * keeps running: a session that passes through `/invite/accept?token=…` resumes recording the
 * moment the app navigates back onto a clean URL, instead of losing the rest of the page load
 * to a finality that was only ever Sentry's sampling model.
 *
 * Residual: a checkout landing inside the ≤250ms between the navigation and the next poll is
 * already in the buffer. In error-sampling mode `stop()` destroys that buffer unflushed
 * (`forceFlush` is false unless `recordingMode === 'session'`) so it never ships; in session
 * mode the pending segment is flushed, and there a full snapshot only happens at start — on a
 * clean URL, by construction — and on session refresh.
 *
 * ponytail: a poll, because none of the ways that URL changes fires an event we could listen
 * for — supabase clears the hash with `history.replaceState`, and react-router pushes. Swap it
 * for a router-level trigger only if 250ms of lost recording ever matters.
 */
export function runReplayOnlyOnCleanUrls(
  start: () => void,
  stop: () => void,
  options?: { resumable?: boolean }
): void {
  let recording = false;

  const step = () => {
    const credentialInUrl = hasUrlCredential(window.location.href);

    if (!recording) {
      if (credentialInUrl) return;
      recording = true;
      start();
      return;
    }

    if (!credentialInUrl) return;
    recording = false;
    stop();
    if (!options?.resumable) clearInterval(timer);
  };

  // Scheduled before the first step so `timer` can be a const the step already closes over —
  // the first tick is POLL_MS away either way, and the step below cannot reach `clearInterval`
  // on its first run (nothing is recording yet).
  const timer = setInterval(step, POLL_MS);
  // Synchronously on the ordinary page load, whose URL is already clean.
  step();
}
